import React, { useEffect } from 'react';
import { Clock, Shield, Zap, Check, ShoppingBasket } from 'lucide-react';
import { motion } from 'framer-motion';
import QuantitySelector from '../components/QuantitySelector';
import { useCart } from '../context/CartContext';

const ProductPage: React.FC = () => {
  const { quantity, setQuantity, openEmbedModal } = useCart();
  const pricePerMinute = 0.01; // €0.01 per minute
  const totalPrice = (pricePerMinute * quantity).toFixed(2);

  useEffect(() => {
  }, [quantity]);

  return (
    <div className="pt-24 pb-20 bg-gray-900">
      <div className="container px-4 mx-auto">
        <div className="flex flex-col lg:flex-row">
          {/* Product Image */}
          <motion.div
            className="mb-12 lg:w-1/2 lg:mb-0"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="overflow-hidden bg-gray-800 rounded-xl shadow-xl">
              <img
                src="/pexels-photo-1779487.webp"
                alt="WinMax Optimizer Software Dashboard"
                className="w-full h-auto"
              />
            </div>

            <div className="grid grid-cols-3 gap-4 mt-8">
              <div className="p-4 bg-gray-800 rounded-lg">
                <img
                  src="/pexels-photo-1181298.webp"
                  alt="WinMax Optimizer Screenshot 1"
                  className="w-full h-auto rounded-md"
                />
              </div>
              <div className="p-4 bg-gray-800 rounded-lg">
                <img
                  src="/pexels-photo-7988079.webp"
                  alt="WinMax Optimizer Screenshot 2"
                  className="w-full h-auto rounded-md"
                />
              </div>
              <div className="p-4 bg-gray-800 rounded-lg">
                <img
                  src="/pexels-photo-577585.webp"
                  alt="WinMax Optimizer Screenshot 3"
                  className="w-full h-auto rounded-md"
                />
              </div>
            </div>
          </motion.div>

          {/* Product Details */}
          <motion.div
            className="lg:w-1/2 lg:pl-12"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="mb-4 text-3xl font-bold text-white md:text-4xl">
              WinMax Optimizer
            </h1>

            <div className="flex items-center mb-6">
              <div className="px-3 py-1 text-sm font-medium rounded-full bg-primary-400/20 text-primary-400">
                Best Seller
              </div>
              <div className="flex ml-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
                <span className="ml-2 text-gray-400">(492 reviews)</span>
              </div>
            </div>

            <div className="mb-8 text-3xl font-bold text-white">
              €{totalPrice}
              <span className="ml-2 text-lg font-normal text-gray-400">for {quantity} minute{quantity > 1 ? 's' : ''}</span>
            </div>

            <p className="mb-8 text-gray-300">
              WinMax Custom Duration gives you complete control over how long your PC optimization lasts.
              Purchase only the minutes you need, and enjoy peak performance for exactly as long as you require.
            </p>

            <div className="mb-8">
              <h3 className="mb-4 text-lg font-semibold text-white">Key Benefits:</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check size={20} className="flex-shrink-0 mt-1 mr-2 text-primary-400" />
                  <span className="text-gray-300">Removes system bottlenecks for immediate performance gains</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="flex-shrink-0 mt-1 mr-2 text-primary-400" />
                  <span className="text-gray-300">Optimizes memory usage to prevent slowdowns and crashes</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="flex-shrink-0 mt-1 mr-2 text-primary-400" />
                  <span className="text-gray-300">Streamlines startup processes for faster boot times</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="flex-shrink-0 mt-1 mr-2 text-primary-400" />
                  <span className="text-gray-300">Automatic updates ensure you always have the latest optimizations</span>
                </li>
              </ul>
            </div>

            <div className="mb-8">
              <label htmlFor="quantity" className="block mb-2 text-sm font-medium text-gray-300">
                Minutes of Optimization:
              </label>
              <div className="flex items-center">
                <QuantitySelector
                  quantity={quantity}
                  onChange={setQuantity}
                  minQuantity={100}
                  maxQuantity={10000000}
                />
              </div>
            </div>

            <div className="flex flex-col mb-8 space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
              <button
                className="flex justify-center items-center px-8 py-3 font-medium text-white rounded-md transition-colors bg-primary-500 hover:bg-primary-600 product-btn"
                onClick={openEmbedModal}
              >
                <ShoppingBasket size={16} className="mr-2" />
                Buy Now
              </button>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="flex items-center">
                <Clock size={18} className="mr-2 text-primary-400" />
                <span className="text-gray-400">Custom Duration</span>
              </div>
              <div className="flex items-center">
                <Shield size={18} className="mr-2 text-primary-400" />
                <span className="text-gray-400">Secure Payment</span>
              </div>
              <div className="flex items-center">
                <Zap size={18} className="mr-2 text-primary-400" />
                <span className="text-gray-400">Instant Activation</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const Star = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

export default ProductPage;